import { Form, Input, Modal, Radio, Space, Typography } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { strings } from '../../../lang';
import { getBookingLaneLabel } from './pre-sales-questionnaire-utils';

/**
 * The reasons a preseller may give for booking outside the Priority-1 lane. The values are the wire contract
 * with the backend (`BookingLaneReasons`); the labels are display-only.
 */
export const LANE_BOOKING_REASONS = {
  noSuitableSlot: 'no_suitable_slot',
  appointmentTypeMismatch: 'appointment_type_mismatch',
  other: 'other',
} as const;

export type BookingLaneReason = (typeof LANE_BOOKING_REASONS)[keyof typeof LANE_BOOKING_REASONS];

export type BookingLaneReasonResult = {
  reason: BookingLaneReason;
  note?: string;
};

type PreSalesBookingLaneReasonModalProps = {
  open: boolean;
  /** The lane the agent is about to book. */
  bookedBookingLane: string;
  /** The first lane of the offered order, i.e. Priority 1. */
  firstBookingLane: string;
  onConfirm: (result: BookingLaneReasonResult) => void;
  onCancel: () => void;
};

const reasonOptions = () => [
  { value: LANE_BOOKING_REASONS.noSuitableSlot, label: strings.preSalesQuestionnaire.laneReasonNoSuitableSlot },
  { value: LANE_BOOKING_REASONS.appointmentTypeMismatch, label: strings.preSalesQuestionnaire.laneReasonAppointmentTypeMismatch },
  { value: LANE_BOOKING_REASONS.other, label: strings.preSalesQuestionnaire.laneReasonOther },
];

/**
 * Asks the agent why they are booking a lane other than the first offered one.
 *
 * Shown before the booking is sent, so the reason travels with the completion payload and the Priority-1
 * booking rate can be measured per reason. A reason is required: cancelling aborts the booking rather than
 * recording it without one, because a defaulted reason is indistinguishable from a real answer.
 */
export const PreSalesBookingLaneReasonModal = ({
  open,
  bookedBookingLane,
  firstBookingLane,
  onConfirm,
  onCancel,
}: PreSalesBookingLaneReasonModalProps) => {
  const [reason, setReason] = useState<BookingLaneReason | undefined>(undefined);
  const [note, setNote] = useState('');
  const [showValidation, setShowValidation] = useState(false);

  // Each opening starts clean, so a previous booking's answer is never reused.
  useEffect(() => {
    if (open) {
      setReason(undefined);
      setNote('');
      setShowValidation(false);
    }
  }, [open, bookedBookingLane]);

  const handleConfirm = useCallback(() => {
    if (!reason) {
      setShowValidation(true);
      return;
    }

    onConfirm({ reason, note: note.trim() || undefined });
  }, [note, onConfirm, reason]);

  return (
    <Modal
      open={open}
      title={strings.preSalesQuestionnaire.laneReasonModalTitle}
      okText={strings.preSalesQuestionnaire.laneReasonConfirm}
      cancelText={strings.preSalesQuestionnaire.laneReasonCancel}
      onOk={handleConfirm}
      onCancel={onCancel}
      destroyOnHidden
      maskClosable={false}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text>
          {strings.preSalesQuestionnaire.laneReasonModalIntro(getBookingLaneLabel(bookedBookingLane), getBookingLaneLabel(firstBookingLane))}
        </Typography.Text>
        <Form layout="vertical" component={false}>
          <Form.Item
            label={strings.preSalesQuestionnaire.laneReasonModalLabel}
            required
            validateStatus={showValidation && !reason ? 'error' : undefined}
            help={showValidation && !reason ? strings.preSalesQuestionnaire.laneReasonRequired : undefined}
          >
            <Radio.Group
              value={reason}
              onChange={(event) => {
                setReason(event.target.value as BookingLaneReason);
                setShowValidation(false);
              }}
            >
              <Space direction="vertical">
                {reasonOptions().map((option) => (
                  <Radio key={option.value} value={option.value}>{option.label}</Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item label={strings.preSalesQuestionnaire.laneReasonNoteLabel}>
            <Input.TextArea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={strings.preSalesQuestionnaire.laneReasonNotePlaceholder}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={500}
            />
          </Form.Item>
        </Form>
      </Space>
    </Modal>
  );
};
